import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { Tabs, NativeSelect } from '@chakra-ui/react'
import { Stack, Box } from '.'

export default function SimpleTabs ({ defaultValue, value, onValueChange, lazyMount: lazyMountProp, tabs, actions, tabProps, variant, wrapped, orientation = 'horizontal', colorPalette }) {
  const { isLazy, ...restTabProps } = tabProps || {}
  const lazyMount = lazyMountProp || isLazy

  const location = useLocation()
  const navigate = useNavigate()
  const firstTab = tabs?.find(Boolean)
  const hasUrlTabs = tabs?.some(tab => tab?.to)

  // When URL-based, derive active tab from current pathname.
  // Sort by path length descending so more-specific paths match before their prefixes
  // (e.g. /participants/groups matches before /participants).
  const urlActiveValue = hasUrlTabs
    ? (() => {
        const sorted = [...(tabs || [])].filter(Boolean).sort((a, b) => (b?.to?.length || 0) - (a?.to?.length || 0))
        const match = sorted.find(tab => tab?.to && location.pathname.startsWith(tab.to))
        return match?.key || match?.title || defaultValue || firstTab?.key || firstTab?.title
      })()
    : null

  // Internal state to track active tab for the mobile select in uncontrolled mode
  const [internalValue, setInternalValue] = useState(defaultValue || firstTab?.key || firstTab?.title)

  function handleValueChange (newValue) {
    setInternalValue(newValue)
    if (hasUrlTabs) {
      const tab = tabs?.find(t => t && (t.key || t.title) === newValue)
      if (tab?.to) navigate(tab.to)
    }
    if (onValueChange) onValueChange(newValue)
  }

  const rootProps = hasUrlTabs
    ? { value: urlActiveValue, onValueChange: e => handleValueChange(e.value) }
    : { value: value ?? internalValue, onValueChange: e => handleValueChange(e.value) }

  // Derive the current value across all modes for the mobile select
  const currentValue = hasUrlTabs ? urlActiveValue : (value ?? internalValue)

  return (
    <Tabs.Root
      {...restTabProps}
      {...rootProps}
      orientation={orientation}
      bg={wrapped && 'bg.surface'}
      borderWidth={wrapped && '1px'}
      borderColor='border.default'
      rounded={wrapped && 'md'}
      shadow={wrapped && 'md'}
      lazyMount={lazyMount}
      variant={variant || (wrapped ? 'enclosed' : 'line')}
      colorPalette={colorPalette || 'primary'}
    >
      {/* Mobile: native select, hidden on md+ */}
      <Stack display={{ base: 'flex', md: 'none' }} direction='row' gap={2} alignItems='center' mb={2}>
        <NativeSelect.Root flex={1}>
          <NativeSelect.Field value={currentValue} onChange={e => handleValueChange(e.target.value)}>
            {tabs?.map(tab => {
              if (!tab) return null
              return <option key={tab.key || tab.title} value={tab.key || tab.title}>{tab.plainTitle || tab.title}</option>
            })}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
        {actions && <Box flexShrink={0}>{actions}</Box>}
      </Stack>

      {/* Desktop: tab list, hidden on mobile */}
      <Stack display={{ base: 'none', md: 'flex' }} direction='row' gap={5} justifyContent='space-between' alignItems='center'>
        <Tabs.List flex={1}>
          {tabs?.map(tab => {
            if (!tab) return null
            return <Tabs.Trigger key={tab.key || tab.title} value={tab.key || tab.title}>{tab.title}</Tabs.Trigger>
          })}
          <Tabs.Indicator rounded='lg' />
        </Tabs.List>
        {actions && <Box flexShrink={0}>{actions}</Box>}
      </Stack>
      {tabs?.map(tab =>
        tab && <Tabs.Content p={wrapped && 3} key={tab.key || tab.title} value={tab.key || tab.title} colorPalette='gray'>{tab.content}</Tabs.Content>
      )}
    </Tabs.Root>
  )
}
